import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { MonthPicker } from '../components/ui/date-picker';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { PlusCircle, X, Trash2, FileUp, ExternalLink, PenTool, ChevronsUpDown, Check, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { usePreferences } from '../context/PreferencesContext';
import { toast } from 'sonner';
import AuthModal from '../components/auth/AuthModal';
import { useMMDLocationsDetailed } from '../hooks/useMMDLocations';
import { cn } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ALL_GRADES = ['MEO Class 1', 'MEO Class 2', 'MEO Class 4', 'ETO', 'Master', 'Chief Mate', '2MFG'];
const NAUTICAL_GRADES = ['Master', 'Chief Mate', '2MFG'];

// Helper to check if grade is Nautical (surveyors can come from any MMD)
const isNauticalGrade = (grade) => NAUTICAL_GRADES.includes(grade);

const emptyQuestion = {
  question_text: '',
  has_answer: false,
  answer_text: '',
  is_decider: false,
  asked_by_internal: false
};

const SubmitQuestionPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { preferences } = usePreferences();
  const [loading, setLoading] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  
  const [surveyors, setSurveyors] = useState([]);
  const [functions, setFunctions] = useState([]);
  
  // Surveyor search popover states
  const [internalSurveyorOpen, setInternalSurveyorOpen] = useState(false);
  const [externalSurveyorOpen, setExternalSurveyorOpen] = useState(false);
  
  // Fetch MMD locations with grade info from API
  const { locations: mmdLocationsDetailed, loading: mmdLoading, getGradesForMMD, getMMDsForGrade } = useMMDLocationsDetailed();
  
  // Common metadata
  const [commonData, setCommonData] = useState({
    grade: preferences.grade || '',
    mmd_location: preferences.mmd_location || '',
    surveyor_internal_id: '',
    surveyor_external_id: '',
    surveyor_internal_other: '',
    surveyor_external_other: '',
    exam_month: ''
  });

  // Get available grades based on selected MMD (must be after commonData declaration)
  const availableGrades = commonData.mmd_location 
    ? getGradesForMMD(commonData.mmd_location)
    : ALL_GRADES;

  // Get available MMDs based on selected grade
  const availableMMDs = getMMDsForGrade(commonData.grade);

  // Bulk submit URL
  const [bulkSubmitUrl, setBulkSubmitUrl] = useState('');
  const [writtenSubmitUrl, setWrittenSubmitUrl] = useState('');

  // Function-wise questions
  const [functionGroups, setFunctionGroups] = useState([
    {
      id: Date.now(),
      function_id: '',
      questions: [{ ...emptyQuestion }]
    }
  ]);

  useEffect(() => {
    // Require authentication to submit questions
    if (!isAuthenticated) {
      toast.error('Please login to submit questions');
      navigate('/profile');
      return;
    }
    
    fetchSurveyors();
    fetchFunctions();
    fetchBulkSubmitUrl();
  }, [isAuthenticated, navigate]);

  const fetchBulkSubmitUrl = async () => {
    try {
      const response = await axios.get(`${API}/pages/external-links`);
      setBulkSubmitUrl(response.data.bulk_submit_url || '');
      setWrittenSubmitUrl(response.data.written_submit_url || '');
    } catch (error) {
      console.error('Error fetching external URLs:', error);
    }
  };

  // Reset grade when MMD changes and current grade is not supported
  // Only validate after MMD data is loaded to prevent false positives
  useEffect(() => {
    if (mmdLoading || mmdLocationsDetailed.length === 0) return; // Wait for data to load
    
    if (commonData.mmd_location && commonData.grade) {
      const supportedGrades = getGradesForMMD(commonData.mmd_location);
      if (!supportedGrades.includes(commonData.grade)) {
        setCommonData(prev => ({ ...prev, grade: '' }));
        toast.info(`Grade "${commonData.grade}" is not available at ${commonData.mmd_location}. Please select another grade.`);
      }
    }
  }, [commonData.mmd_location, commonData.grade, getGradesForMMD, mmdLoading, mmdLocationsDetailed.length]);

  // Reset MMD when grade changes and current MMD doesn't support the new grade
  // Only validate after MMD data is loaded to prevent false positives
  useEffect(() => {
    if (mmdLoading || mmdLocationsDetailed.length === 0) return; // Wait for data to load
    
    if (commonData.grade && commonData.mmd_location) {
      const supportedMMDs = getMMDsForGrade(commonData.grade);
      if (!supportedMMDs.includes(commonData.mmd_location)) {
        setCommonData(prev => ({ ...prev, mmd_location: '' }));
        toast.info(`${commonData.mmd_location} doesn't support ${commonData.grade}. Please select another MMD.`);
      }
    }
  }, [commonData.grade, commonData.mmd_location, getMMDsForGrade, mmdLoading, mmdLocationsDetailed.length]);

  // Reset surveyor selections when grade or MMD changes
  useEffect(() => {
    if (commonData.grade || commonData.mmd_location) {
      // Check if current internal surveyor is still valid
      if (commonData.surveyor_internal_id && commonData.surveyor_internal_id !== 'others') {
        const surveyor = surveyors.find(s => s.id === commonData.surveyor_internal_id);
        const mmd = commonData.mmd_location;
        const mmdNormalized = mmd?.endsWith(' MMD') ? mmd : `${mmd} MMD`;
        const isValid = surveyor && 
          (!commonData.grade || surveyor.grades?.includes(commonData.grade)) &&
          (!mmd || surveyor.mmd_locations?.includes(mmd) || surveyor.mmd_locations?.includes(mmdNormalized));
        
        if (!isValid) {
          setCommonData(prev => ({ ...prev, surveyor_internal_id: '' }));
        }
      }
      
      // Check if current external surveyor is still valid
      if (commonData.surveyor_external_id && commonData.surveyor_external_id !== 'others') {
        const surveyor = surveyors.find(s => s.id === commonData.surveyor_external_id);
        const mmd = commonData.mmd_location;
        const mmdNormalized = mmd?.endsWith(' MMD') ? mmd : `${mmd} MMD`;
        const isValid = surveyor && 
          (!commonData.grade || surveyor.grades?.includes(commonData.grade)) &&
          (!mmd || surveyor.mmd_locations?.includes(mmd) || surveyor.mmd_locations?.includes(mmdNormalized));
        
        if (!isValid) {
          setCommonData(prev => ({ ...prev, surveyor_external_id: '' }));
        }
      }
    }
  }, [commonData.grade, commonData.mmd_location, surveyors]);

  // Reset function selections when grade changes
  useEffect(() => {
    if (commonData.grade) {
      setFunctionGroups(prevGroups => 
        prevGroups.map(group => {
          // Check if current function is still valid for the grade
          if (group.function_id) {
            const func = functions.find(f => f.id === group.function_id);
            if (func && func.grade !== commonData.grade) {
              return { ...group, function_id: '' };
            }
          }
          return group;
        })
      );
    }
  }, [commonData.grade, functions]);

  const fetchSurveyors = async () => {
    try {
      // Only fetch active surveyors for question submission
      const response = await axios.get(`${API}/surveyors?is_active=true`);
      setSurveyors(response.data);
    } catch (error) {
      console.error('Failed to fetch surveyors:', error);
    }
  };

  const fetchFunctions = async () => {
    try {
      const response = await axios.get(`${API}/functions`);
      setFunctions(response.data);
    } catch (error) {
      console.error('Failed to fetch functions:', error);
    }
  };

  const addQuestionToFunction = (groupIndex) => {
    const newGroups = [...functionGroups];
    newGroups[groupIndex].questions.push({ ...emptyQuestion });
    setFunctionGroups(newGroups);
  };

  const removeQuestionFromFunction = (groupIndex, questionIndex) => {
    const newGroups = [...functionGroups];
    if (newGroups[groupIndex].questions.length > 1) {
      newGroups[groupIndex].questions.splice(questionIndex, 1);
      setFunctionGroups(newGroups);
    }
  };

  const addFunctionGroup = () => {
    setFunctionGroups([
      ...functionGroups,
      {
        id: Date.now(),
        function_id: '',
        questions: [{ ...emptyQuestion }]
      }
    ]);
  };

  const removeFunctionGroup = (groupIndex) => {
    if (functionGroups.length > 1) {
      const newGroups = [...functionGroups];
      newGroups.splice(groupIndex, 1);
      setFunctionGroups(newGroups);
    }
  };

  const updateFunctionGroup = (groupIndex, field, value) => {
    const newGroups = [...functionGroups];
    newGroups[groupIndex][field] = value;
    setFunctionGroups(newGroups);
  };

  const updateQuestion = (groupIndex, questionIndex, field, value) => {
    const newGroups = [...functionGroups];
    newGroups[groupIndex].questions[questionIndex][field] = value;
    setFunctionGroups(newGroups);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated()) {
      setShowAuth(true);
      return;
    }

    // Validate all function groups have function selected
    for (let group of functionGroups) {
      if (!group.function_id) {
        toast.error('Please select a function for all question groups');
        return;
      }
      
      // Validate all questions have question text
      for (let question of group.questions) {
        if (!question.question_text.trim()) {
          toast.error('Please fill in all question texts');
          return;
        }
      }
    }

    setLoading(true);
    try {
      // Generate a unique submission batch ID for this submission
      const submissionBatchId = `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Submit each function group separately with the same batch ID
      for (let group of functionGroups) {
        await axios.post(`${API}/questions/bulk`, {
          ...commonData,
          function_id: group.function_id,
          questions: group.questions,
          submission_batch_id: submissionBatchId
        });
      }
      
      toast.success(`Successfully submitted ${functionGroups.reduce((acc, g) => acc + g.questions.length, 0)} questions!`);
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to submit questions:', error);
      
      // Handle validation errors properly
      let errorMessage = 'Failed to submit questions';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          // Pydantic validation errors
          errorMessage = detail.map(err => err.msg || err).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Filter surveyors based on selected grade and MMD
  // For Nautical grades: Show ALL surveyors for the grade (any MMD)
  // For Engineering grades: Show surveyors filtered by Grade + MMD
  const filterSurveyors = (surveyorsList) => {
    return surveyorsList.filter(s => {
      // Check if surveyor has the selected grade
      const hasGrade = commonData.grade ? s.grades?.includes(commonData.grade) : true;
      
      // For Nautical grades, skip MMD filter - surveyors can come from any MMD
      if (isNauticalGrade(commonData.grade)) {
        return hasGrade;
      }
      
      // For Engineering grades, also check MMD location
      // Handle both old format ("Kolkata") and new format ("Kolkata MMD")
      const mmd = commonData.mmd_location;
      const mmdNormalized = mmd?.endsWith(' MMD') ? mmd : `${mmd} MMD`;
      const hasMMD = mmd ? (s.mmd_locations?.includes(mmd) || s.mmd_locations?.includes(mmdNormalized)) : true;
      return hasGrade && hasMMD;
    });
  };

  const internalSurveyors = filterSurveyors(surveyors.filter(s => s.type === 'internal'));
  const externalSurveyors = filterSurveyors(surveyors.filter(s => s.type === 'external'));
  
  // Filter functions based on selected grade
  // Base filtered functions based on grade
  const baseFunctions = commonData.grade 
    ? functions.filter(f => f.grade === commonData.grade && f.exam_type !== 'written')
    : functions.filter(f => f.exam_type !== 'written');

  // Get available functions for a specific group (excluding already selected in other groups)
  const getAvailableFunctions = (currentGroupIndex) => {
    const selectedFunctionIds = functionGroups
      .filter((_, index) => index !== currentGroupIndex)  // Exclude current group
      .map(group => group.function_id)
      .filter(id => id);  // Remove empty selections
    
    return baseFunctions.filter(func => !selectedFunctionIds.includes(func.id));
  };

  return (
    <Layout>
      <div className="page-transition max-w-5xl mx-auto">
        <Card className="shadow-lg">
          <CardHeader className="bg-gradient-to-r from-[#0A7EA4]/5 to-[#14B8A6]/5">
            <div className="flex flex-col gap-4">
              <div>
                <CardTitle className="text-2xl md:text-3xl text-[#0A7EA4]">Submit Questions</CardTitle>
                <p className="text-gray-600 text-sm mt-2">
                  Contribute multiple questions across different functions from your exam experience
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                {writtenSubmitUrl && (
                  <a href={writtenSubmitUrl} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                    <Button variant="outline" className="w-full sm:w-auto flex items-center justify-center gap-2 border-[#EC4899] text-[#EC4899] hover:bg-[#EC4899]/10">
                      <PenTool className="w-4 h-4" />
                      Submit Written
                      <ExternalLink className="w-3 h-3" />
                    </Button>
                  </a>
                )}
                {bulkSubmitUrl && (
                  <a href={bulkSubmitUrl} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                    <Button variant="outline" className="w-full sm:w-auto flex items-center justify-center gap-2">
                      <FileUp className="w-4 h-4" />
                      Bulk Submit
                      <ExternalLink className="w-3 h-3" />
                    </Button>
                  </a>
                )}
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-4 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Common Metadata Section */}
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                  <span className="w-8 h-8 bg-[#0A7EA4] text-white rounded-full flex items-center justify-center text-sm">1</span>
                  Common Information
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Grade */}
                  <div>
                    <Label htmlFor="grade">Grade *</Label>
                    <Select
                      value={commonData.grade}
                      onValueChange={(value) => setCommonData({ ...commonData, grade: value })}
                      required
                    >
                      <SelectTrigger data-testid="submit-grade" className="mt-1">
                        <SelectValue placeholder="Select grade" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableGrades.map((grade) => (
                          <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {commonData.mmd_location && availableGrades.length < ALL_GRADES.length && (
                      <p className="text-xs text-amber-600 mt-1">
                        Showing grades available at {commonData.mmd_location}
                      </p>
                    )}
                  </div>

                  {/* MMD Location */}
                  <div>
                    <Label htmlFor="mmd_location">MMD Location *</Label>
                    <Select
                      value={commonData.mmd_location}
                      onValueChange={(value) => setCommonData({ ...commonData, mmd_location: value })}
                      required
                    >
                      <SelectTrigger data-testid="submit-mmd" className="mt-1">
                        <SelectValue placeholder="Select MMD location" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableMMDs.map((location) => (
                          <SelectItem key={location} value={location}>{location}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {commonData.grade && availableMMDs.length < mmdLocationsDetailed.length && (
                      <p className="text-xs text-amber-600 mt-1">
                        Showing MMDs available for {commonData.grade}
                      </p>
                    )}
                  </div>
                </div>

                {/* Surveyors */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Internal Surveyor</Label>
                    <Popover open={internalSurveyorOpen} onOpenChange={setInternalSurveyorOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={internalSurveyorOpen}
                          data-testid="submit-internal-surveyor"
                          className="w-full justify-between mt-1 font-normal"
                        >
                          {commonData.surveyor_internal_id === 'others' 
                            ? 'Others (specify below)'
                            : commonData.surveyor_internal_id
                              ? internalSurveyors.find(s => s.id === commonData.surveyor_internal_id)?.name || 'Select surveyor'
                              : 'Select surveyor'}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search surveyor..." />
                          <CommandList>
                            <CommandEmpty>
                              {internalSurveyors.length === 0 
                                ? `No surveyors for ${commonData.grade || 'selected grade'} at ${commonData.mmd_location || 'selected MMD'}`
                                : 'No surveyor found'}
                            </CommandEmpty>
                            <CommandGroup>
                              {internalSurveyors.map((surveyor) => (
                                <CommandItem
                                  key={surveyor.id}
                                  value={surveyor.name}
                                  onSelect={() => {
                                    setCommonData({ ...commonData, surveyor_internal_id: surveyor.id });
                                    setInternalSurveyorOpen(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      commonData.surveyor_internal_id === surveyor.id ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {surveyor.name}
                                </CommandItem>
                              ))}
                              <CommandItem
                                value="others"
                                onSelect={() => {
                                  setCommonData({ ...commonData, surveyor_internal_id: 'others' });
                                  setInternalSurveyorOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    commonData.surveyor_internal_id === 'others' ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                Others (specify below)
                              </CommandItem>
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {commonData.surveyor_internal_id === 'others' && (
                      <Input
                        data-testid="submit-internal-other"
                        className="mt-2"
                        placeholder="Enter surveyor name"
                        value={commonData.surveyor_internal_other}
                        onChange={(e) => setCommonData({ ...commonData, surveyor_internal_other: e.target.value })}
                      />
                    )}
                  </div>

                  <div>
                    <Label>External Surveyor</Label>
                    <Popover open={externalSurveyorOpen} onOpenChange={setExternalSurveyorOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={externalSurveyorOpen}
                          data-testid="submit-external-surveyor"
                          className="w-full justify-between mt-1 font-normal"
                        >
                          {commonData.surveyor_external_id === 'others' 
                            ? 'Others (specify below)'
                            : commonData.surveyor_external_id
                              ? externalSurveyors.find(s => s.id === commonData.surveyor_external_id)?.name || 'Select surveyor'
                              : 'Select surveyor'}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search surveyor..." />
                          <CommandList>
                            <CommandEmpty>
                              {externalSurveyors.length === 0 
                                ? `No surveyors for ${commonData.grade || 'selected grade'} at ${commonData.mmd_location || 'selected MMD'}`
                                : 'No surveyor found'}
                            </CommandEmpty>
                            <CommandGroup>
                              {externalSurveyors.map((surveyor) => (
                                <CommandItem
                                  key={surveyor.id}
                                  value={surveyor.name}
                                  onSelect={() => {
                                    setCommonData({ ...commonData, surveyor_external_id: surveyor.id });
                                    setExternalSurveyorOpen(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      commonData.surveyor_external_id === surveyor.id ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {surveyor.name}
                                </CommandItem>
                              ))}
                              <CommandItem
                                value="others"
                                onSelect={() => {
                                  setCommonData({ ...commonData, surveyor_external_id: 'others' });
                                  setExternalSurveyorOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    commonData.surveyor_external_id === 'others' ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                Others (specify below)
                              </CommandItem>
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {commonData.surveyor_external_id === 'others' && (
                      <Input
                        data-testid="submit-external-other"
                        className="mt-2"
                        placeholder="Enter surveyor name"
                        value={commonData.surveyor_external_other}
                        onChange={(e) => setCommonData({ ...commonData, surveyor_external_other: e.target.value })}
                      />
                    )}
                  </div>
                </div>

                {/* Exam Month */}
                <div>
                  <Label htmlFor="exam_month">Exam Month *</Label>
                  <div className="mt-1">
                    <MonthPicker
                      value={commonData.exam_month}
                      onChange={(value) => setCommonData({ ...commonData, exam_month: value })}
                      placeholder="Select exam month"
                      fromYear={2015}
                      toYear={new Date().getFullYear()}
                      maxMonth={`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`}
                    />
                  </div>
                </div>
              </div>

              <Separator className="my-8" />

              {/* Function Groups Section */}
              <div className="space-y-8">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                    <span className="w-8 h-8 bg-[#14B8A6] text-white rounded-full flex items-center justify-center text-sm">2</span>
                    Questions by Function
                  </h3>
                </div>

                {functionGroups.map((group, groupIndex) => (
                  <Card key={group.id} className="border-2 border-gray-200">
                    <CardHeader className="bg-gray-50">
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-lg">Function {groupIndex + 1}</CardTitle>
                        {functionGroups.length > 1 && (
                          <Button
                            type="button"
                            onClick={() => removeFunctionGroup(groupIndex)}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`remove-function-${groupIndex}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Remove Function
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="p-6 space-y-6">
                      {/* Function Selection */}
                      <div>
                        <Label>Function *</Label>
                        {(() => {
                          const availableFunctions = getAvailableFunctions(groupIndex);
                          return (
                            <>
                              <Select
                                value={group.function_id}
                                onValueChange={(value) => updateFunctionGroup(groupIndex, 'function_id', value)}
                                required
                                disabled={!commonData.grade}
                              >
                                <SelectTrigger data-testid={`function-select-${groupIndex}`} className="mt-1">
                                  <SelectValue placeholder={commonData.grade ? "Select function" : "Select grade first"} />
                                </SelectTrigger>
                                <SelectContent>
                                  {availableFunctions.length === 0 ? (
                                    <div className="p-2 text-sm text-gray-500">
                                      {baseFunctions.length === 0 
                                        ? `No functions available for ${commonData.grade}`
                                        : 'All functions have been selected'}
                                    </div>
                                  ) : (
                                    availableFunctions.map((func) => {
                                      const suffix = func.function_suffix && func.function_suffix !== 'None' && func.function_suffix !== 'null' ? func.function_suffix : '';
                                      return (
                                        <SelectItem key={func.id} value={func.id}>
                                          Function {func.function_number}{suffix} - {func.name}
                                        </SelectItem>
                                      );
                                    })
                                  )}
                                </SelectContent>
                              </Select>
                              {commonData.grade && baseFunctions.length === 0 && (
                                <p className="text-xs text-amber-600 mt-1">
                                  No functions configured for {commonData.grade}. Please contact admin.
                                </p>
                              )}
                            </>
                          );
                        })()}
                      </div>

                      <Separator />

                      {/* Questions for this function */}
                      <div className="space-y-6">
                        {group.questions.map((question, questionIndex) => (
                          <div key={questionIndex} className="space-y-4 p-4 border border-gray-200 rounded-lg bg-white">
                            <div className="flex justify-between items-center">
                              <h4 className="font-semibold text-gray-700">Question {questionIndex + 1}</h4>
                              {group.questions.length > 1 && (
                                <Button
                                  type="button"
                                  onClick={() => removeQuestionFromFunction(groupIndex, questionIndex)}
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700"
                                  data-testid={`remove-question-${groupIndex}-${questionIndex}`}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              )}
                            </div>

                            {/* Question Text */}
                            <div>
                              <Label>Question *</Label>
                              <Textarea
                                className="mt-1"
                                rows={3}
                                placeholder="Enter the question..."
                                value={question.question_text}
                                onChange={(e) => updateQuestion(groupIndex, questionIndex, 'question_text', e.target.value)}
                                required
                                data-testid={`question-text-${groupIndex}-${questionIndex}`}
                              />
                            </div>

                            {/* Checkboxes */}
                            <div className="flex gap-6 flex-wrap">
                              <div className="flex items-center space-x-2">
                                <Checkbox
                                  id={`has-answer-${groupIndex}-${questionIndex}`}
                                  checked={question.has_answer}
                                  onCheckedChange={(checked) => updateQuestion(groupIndex, questionIndex, 'has_answer', checked)}
                                  data-testid={`has-answer-${groupIndex}-${questionIndex}`}
                                />
                                <Label htmlFor={`has-answer-${groupIndex}-${questionIndex}`} className="cursor-pointer text-sm">
                                  I know the answer
                                </Label>
                              </div>

                              <div className="flex items-center space-x-2">
                                <Checkbox
                                  id={`is-decider-${groupIndex}-${questionIndex}`}
                                  checked={question.is_decider}
                                  onCheckedChange={(checked) => updateQuestion(groupIndex, questionIndex, 'is_decider', checked)}
                                  data-testid={`is-decider-${groupIndex}-${questionIndex}`}
                                />
                                <Label htmlFor={`is-decider-${groupIndex}-${questionIndex}`} className="cursor-pointer text-sm">
                                  Decider Question
                                </Label>
                              </div>

                              <div className="flex items-center space-x-2">
                                <Checkbox
                                  id={`asked-by-internal-${groupIndex}-${questionIndex}`}
                                  checked={question.asked_by_internal || false}
                                  onCheckedChange={(checked) => updateQuestion(groupIndex, questionIndex, 'asked_by_internal', checked)}
                                  data-testid={`asked-by-internal-${groupIndex}-${questionIndex}`}
                                />
                                <Label htmlFor={`asked-by-internal-${groupIndex}-${questionIndex}`} className="cursor-pointer text-sm">
                                  Asked by Internal
                                </Label>
                              </div>
                            </div>

                            {/* Answer Text */}
                            {question.has_answer && (
                              <div>
                                <Label>Answer</Label>
                                <Textarea
                                  className="mt-1"
                                  rows={6}
                                  placeholder="Write the complete answer here (include explanations, details, references, sources)..."
                                  value={question.answer_text}
                                  onChange={(e) => updateQuestion(groupIndex, questionIndex, 'answer_text', e.target.value)}
                                  data-testid={`answer-text-${groupIndex}-${questionIndex}`}
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                  Include all details, explanations, and references in one place
                                </p>
                              </div>
                            )}
                          </div>
                        ))}

                        <Button
                          type="button"
                          onClick={() => addQuestionToFunction(groupIndex)}
                          variant="outline"
                          className="w-full text-[#0A7EA4] border-[#0A7EA4] hover:bg-[#0A7EA4]/10"
                          data-testid={`add-question-${groupIndex}`}
                        >
                          <PlusCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                          <span className="truncate">Add Another Question</span>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                
                {/* Add Another Function Button - at bottom for better UX */}
                <Button
                  type="button"
                  onClick={addFunctionGroup}
                  variant="outline"
                  className="w-full text-[#14B8A6] border-[#14B8A6] border-dashed hover:bg-[#14B8A6]/10"
                  data-testid="add-function-group"
                >
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Add Another Function
                </Button>
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="flex-1"
                  data-testid="submit-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#0A7EA4] hover:bg-[#0A7EA4]/90"
                  disabled={loading}
                  data-testid="submit-all-btn"
                >
                  {loading ? 'Submitting...' : `Submit All Questions (${functionGroups.reduce((acc, g) => acc + g.questions.length, 0)})`}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
    </Layout>
  );
};

export default SubmitQuestionPage;